import ATTACH_CREDIT_DOCUMENTS from "@/core/graphql/credit/attachCreditDocuments";
import DELETE_CREDIT_DOCUMENTS from "@/core/graphql/credit/deleteCreditDocuments";
import { GET_CREDIT_BY_REMITTANCE_ITEM_ID } from "@/core/graphql/credit";
import { Credit } from "@/core/entities/credit";
import {
  GetCreditParams,
  GetCreditResponse,
  ICreditAttachDocumentsParams,
  ICreditAttachDocumentsResponse,
  ICreditDeleteDocumentsParams,
  ICreditDeleteDocumentsResponse,
  ICreditRepository,
} from "@/core/repositories/credit";
import { ApplicationError } from "@/infrastructure/errors/ApplicationError";
import { createLogger } from "@/infrastructure/services/logger";
import { ApolloCache, ApolloClient, NormalizedCacheObject } from "@apollo/client";

export class CreditRepository implements ICreditRepository {
  private readonly logger = createLogger("CreditRepository");

  constructor(private readonly client: ApolloClient) { }

  async getCredit(params: GetCreditParams): Promise<GetCreditResponse> {
    const { data } = await this.client.query<GetCreditResponse>({
      query: GET_CREDIT_BY_REMITTANCE_ITEM_ID,
      variables: { params },
    });

    if (!data) {
      throw new ApplicationError("Failed to get credit");
    }

    return data;
  }

  public async attachCreditDocuments(
    params: ICreditAttachDocumentsParams,
    currentPage: number
  ): Promise<ICreditAttachDocumentsResponse> {
    const { data } = await this.client.mutate<ICreditAttachDocumentsResponse>({
      mutation: ATTACH_CREDIT_DOCUMENTS,
      variables: { params },
      update: (cache, { data: mutationData }) => {
        if (mutationData?.attachCreditDocument) {
          this.evictCacheForCreditAndRemittance(cache, mutationData.attachCreditDocument.remittanceItemId, currentPage);
        }
      },
    });

    if (!data) {
      throw new Error("Failed to attach documents to credit");
    }

    return {
      attachCreditDocument: new Credit(data.attachCreditDocument),
    };
  }

  public async deleteCreditDocuments(
    params: ICreditDeleteDocumentsParams,
    currentPage: number
  ): Promise<ICreditDeleteDocumentsResponse> {
    const { data } = await this.client.mutate<ICreditDeleteDocumentsResponse>({
      mutation: DELETE_CREDIT_DOCUMENTS,
      variables: { params },
      update: (cache, { data: mutationData }) => {
        if (mutationData?.deleteCreditDocuments) {
          this.evictCacheForCreditAndRemittance(cache, mutationData.deleteCreditDocuments.remittanceItemId, currentPage);
        }
      },
    });

    if (!data) {
      throw new Error("Failed to delete credit documents");
    }

    return { deleteCreditDocuments: new Credit(data.deleteCreditDocuments) };
  }

  private evictCacheForCreditAndRemittance(cache: ApolloCache, remittanceItemId: number, currentPage: number) {
    const rawCache = cache.extract() as NormalizedCacheObject;
    const rootQuery = rawCache?.ROOT_QUERY;
    if (!rootQuery) return;

    for (const key of Object.keys(rootQuery)) {
      this.evictPaginatedField(cache, key, "remittances", currentPage);
      this.evictPaginatedField(cache, key, "remittanceItems", currentPage);
    }

    this.evictCredit(cache, remittanceItemId);
    cache.gc();
  }

  private evictPaginatedField(
    cache: ApolloCache,
    key: string,
    fieldName: "remittances" | "remittanceItems",
    currentPage: number
  ) {
    const match = new RegExp(String.raw`^${fieldName}\((.*)\)$`).exec(key);
    if (!match) return;

    const args = this.safeParseCacheArgs(match[1], fieldName, key);
    if (!args || args.params?.page_number !== currentPage) return;

    cache.evict({
      id: "ROOT_QUERY",
      fieldName,
      args,
    });
  }

  private safeParseCacheArgs(
    matchGroup: string,
    fieldName: string,
    key: string
  ): { params?: { page_number?: number } } | null {
    try {
      return JSON.parse(matchGroup) as { params?: { page_number?: number } };
    } catch (e) {
      this.logger.warn(`CreditRepository: Failed to parse ${fieldName} key:`, key, e);
      return null;
    }
  }

  private evictCredit(cache: ApolloCache, remittanceItemId: number) {
    cache.evict({
      id: "ROOT_QUERY",
      fieldName: "credit",
      args: {
        params: { remittanceItemId },
      },
    });
  }
}
